import React, { useEffect, useMemo, useState } from "react";
import {
  FileText,
  ClipboardList,
  PackageSearch,
  CheckSquare,
  PhoneCall,
  Loader2,
  Search,
  Download,
  X,
} from "lucide-react";
import { api } from "../lib/api.js";
import { fmtDateTime, fmtRelative } from "../lib/format.js";

const KIND_META = {
  som_opened: { icon: FileText, color: "#1F3864", label: "Obra aberta" },
  service_case_status: { icon: FileText, color: "#1F3864", label: "SOM" },
  pcp_opened: { icon: ClipboardList, color: "#B45309", label: "PCP criada" },
  classification_request_status: { icon: ClipboardList, color: "#B45309", label: "PCP" },
  pec_opened: { icon: PackageSearch, color: "#28A745", label: "PEC criada" },
  parts_request_status: { icon: PackageSearch, color: "#28A745", label: "PEC" },
  task_status: { icon: CheckSquare, color: "#6C757D", label: "Tarefa" },
  contact: { icon: PhoneCall, color: "#0D9488", label: "Contacto cliente" },
};

const FILTERS = [
  { key: "all", label: "Todos", kinds: null, color: "#1F3864" },
  { key: "som", label: "SOM", kinds: ["som_opened", "service_case_status"], color: "#1F3864" },
  { key: "pcp", label: "PCP", kinds: ["pcp_opened", "classification_request_status"], color: "#B45309" },
  { key: "pec", label: "PEC", kinds: ["pec_opened", "parts_request_status"], color: "#28A745" },
  { key: "tasks", label: "Tarefas", kinds: ["task_status"], color: "#6C757D" },
  { key: "contacts", label: "Contactos", kinds: ["contact"], color: "#0D9488" },
];

const PAGE_SIZE = 20;

function escapeCsv(v) {
  if (v == null) return "";
  const s = String(v).replace(/"/g, '""');
  return /[",\n]/.test(s) ? `"${s}"` : s;
}

function downloadCsv(filename, rows, headers) {
  const lines = [headers.map(escapeCsv).join(",")];
  for (const r of rows) lines.push(headers.map((h) => escapeCsv(r[h])).join(","));
  const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function EventTimeline({ caseId, caseNumber }) {
  const [events, setEvents] = useState(null);
  const [err, setErr] = useState("");
  const [active, setActive] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setEvents(null);
    setPage(1);
    setSearch("");
    setActive("all");
    api
      .get(`/service-cases/${caseId}/timeline`)
      .then((r) => setEvents(r.data))
      .catch((e) => setErr(e.message || "Falha a carregar histórico"));
  }, [caseId]);

  const counts = useMemo(() => {
    const c = { all: events?.length || 0 };
    for (const f of FILTERS) {
      if (!f.kinds) continue;
      c[f.key] = events ? events.filter((e) => f.kinds.includes(e.kind)).length : 0;
    }
    return c;
  }, [events]);

  const filtered = useMemo(() => {
    if (!events) return [];
    const f = FILTERS.find((x) => x.key === active);
    let arr = !f || !f.kinds ? events : events.filter((e) => f.kinds.includes(e.kind));
    const q = search.trim().toLowerCase();
    if (q) {
      arr = arr.filter(
        (e) =>
          (e.title || "").toLowerCase().includes(q) ||
          (e.detail || "").toLowerCase().includes(q) ||
          (e.user_name || "").toLowerCase().includes(q) ||
          (e.record_number || "").toLowerCase().includes(q)
      );
    }
    return arr;
  }, [events, active, search]);

  useEffect(() => {
    setPage(1);
  }, [active, search]);

  const visible = filtered.slice(0, page * PAGE_SIZE);
  const hasMore = filtered.length > visible.length;

  const exportCsv = () => {
    const rows = filtered.map((e) => ({
      timestamp: e.ts,
      kind: e.kind,
      record: e.record_number || "",
      title: e.title,
      detail: e.detail || "",
      user: e.user_name || "",
    }));
    downloadCsv(
      `historico-${caseNumber || "SOM"}.csv`,
      rows,
      ["timestamp", "kind", "record", "title", "detail", "user"]
    );
  };

  return (
    <div
      className="bg-white border border-gray-200 rounded-md p-4 mb-4"
      data-testid="som-event-timeline"
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-[#1A1A2E]">
          Histórico de eventos
        </h3>
        <div className="flex items-center gap-2">
          {events && (
            <span className="text-[10px] uppercase tracking-widest text-[#6C757D]">
              {filtered.length} de {events.length} evento{events.length === 1 ? "" : "s"}
            </span>
          )}
          {events && events.length > 0 && (
            <button
              onClick={exportCsv}
              className="inline-flex items-center gap-1 text-[11px] text-[#1F3864] border border-gray-300 hover:border-[#1F3864] rounded-sm px-2 py-1"
              data-testid="event-export-csv"
              title="Exportar histórico para CSV"
            >
              <Download className="w-3 h-3" /> CSV
            </button>
          )}
        </div>
      </div>

      {events && events.length > 0 && (
        <>
          <div className="flex flex-wrap gap-1.5 mb-2" data-testid="event-filters">
            {FILTERS.map((f) => {
              const c = counts[f.key] ?? 0;
              const isActive = active === f.key;
              const dimmed = !isActive && c === 0;
              return (
                <button
                  key={f.key}
                  onClick={() => setActive(f.key)}
                  disabled={dimmed}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border transition ${
                    isActive
                      ? "text-white border-transparent"
                      : dimmed
                      ? "text-[#CBD5E1] border-gray-200 cursor-not-allowed"
                      : "text-[#1A1A2E] border-gray-300 hover:border-[#1F3864]"
                  }`}
                  style={isActive ? { backgroundColor: f.color } : undefined}
                  data-testid={`event-filter-${f.key}`}
                >
                  <span>{f.label}</span>
                  <span
                    className={`px-1 rounded-sm text-[10px] ${
                      isActive ? "bg-black/20" : "bg-[#F4F6F8] text-[#6C757D]"
                    }`}
                  >
                    {c}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="relative mb-3">
            <Search className="w-3.5 h-3.5 absolute left-2 top-2 text-[#94A3B8]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Pesquisar no histórico (palavra, número, utilizador)…"
              className="w-full pl-7 pr-7 py-1.5 text-xs border border-gray-300 rounded-sm focus:outline-none focus:border-[#1F3864]"
              data-testid="event-search"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-2 top-1.5 text-[#94A3B8] hover:text-[#1F3864]"
                data-testid="event-search-clear"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        </>
      )}

      {err && (
        <div className="px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm">
          {err}
        </div>
      )}

      {!events && !err && (
        <div className="flex items-center gap-2 text-sm text-[#6C757D]">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> A carregar histórico…
        </div>
      )}

      {events && events.length === 0 && (
        <div className="text-sm text-[#6C757D]">Sem eventos registados ainda.</div>
      )}

      {events && events.length > 0 && filtered.length === 0 && (
        <div className="text-sm text-[#6C757D] italic">Sem eventos para este filtro.</div>
      )}

      {visible.length > 0 && (
        <ol className="relative border-l border-gray-200 ml-2 pl-4 space-y-3">
          {visible.map((e, i) => {
            const meta = KIND_META[e.kind] || { icon: FileText, color: "#6C757D", label: e.kind };
            const Icon = meta.icon;
            return (
              <li key={`${e.ts}-${i}`} className="relative" data-testid={`event-${i}`}>
                <span
                  className="absolute -left-[26px] top-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full border border-white"
                  style={{ backgroundColor: meta.color }}
                >
                  <Icon className="w-2.5 h-2.5 text-white" />
                </span>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm text-[#1A1A2E] font-medium">{e.title}</span>
                  <time
                    className="text-[10px] uppercase tracking-widest text-[#6C757D] whitespace-nowrap"
                    title={fmtDateTime(e.ts)}
                  >
                    {fmtRelative(e.ts)}
                  </time>
                </div>
                {e.detail && (
                  <div className="text-xs text-[#6C757D] mt-0.5 line-clamp-2">{e.detail}</div>
                )}
                {e.user_name && (
                  <div className="text-[10px] text-[#94A3B8] mt-0.5">por {e.user_name}</div>
                )}
              </li>
            );
          })}
        </ol>
      )}

      {hasMore && (
        <div className="mt-3 flex justify-center">
          <button
            onClick={() => setPage((p) => p + 1)}
            className="text-xs text-[#1F3864] border border-gray-300 hover:border-[#1F3864] rounded-sm px-3 py-1.5"
            data-testid="event-load-more"
          >
            Carregar mais {Math.min(PAGE_SIZE, filtered.length - visible.length)} eventos
          </button>
        </div>
      )}
    </div>
  );
}
